import {
  PushNotifications,
  type Token,
  type RegistrationError,
  type PushNotificationSchema,
  type ActionPerformed,
} from "@capacitor/push-notifications";
import { getDeviceInfo } from "hub/src/utils/getDeviceInfo";

export const registerPushNotifications = async () => {
  let permissionStatus = await PushNotifications.checkPermissions();

  if (permissionStatus.receive === "prompt") {
    permissionStatus = await PushNotifications.requestPermissions();
  }

  if (permissionStatus.receive !== "granted") {
    throw new Error("User denied push notifications permissions!");
  }

  await PushNotifications.register();
};

export type PushNotificationEventListeners = {
  registration: (token: Token) => void;
  registrationError: (error: RegistrationError) => void;
  pushNotificationReceived: (notification: PushNotificationSchema) => void;
  pushNotificationActionPerformed: (action: ActionPerformed) => void;
};

export const addPushNotificationsListeners = async (
  listeners?: Partial<PushNotificationEventListeners>,
) => {
  await PushNotifications.addListener("registration", async (token) => {
    console.info("Registration token: ", token.value);
    listeners?.registration?.(token);
  });

  await PushNotifications.addListener("registrationError", (error) => {
    console.error("Registration error: ", JSON.stringify(error));
    listeners?.registrationError?.(error);
  });

  await PushNotifications.addListener(
    "pushNotificationReceived",
    (notification) => {
      console.log("Push notification received: ", notification);
      listeners?.pushNotificationReceived?.(notification);
    },
  );

  await PushNotifications.addListener(
    "pushNotificationActionPerformed",
    (action) => {
      console.log("Push notification action performed", action);
      listeners?.pushNotificationActionPerformed?.(action);
    },
  );
};

export const initializePushNotifications = async () => {
  const device = getDeviceInfo();

  if (!device.isNativeApp) {
    return;
  }

  try {
    await registerPushNotifications();
    await addPushNotificationsListeners();
  } catch (error) {
    console.error("Push Notifications initialization error: ", error);
  }
};

// Unregister push notifications
export const unregisterPushNotifications = async () => {
  await PushNotifications.unregister();
};
